import {
  type ExpertUpdateInput,
  SegmentationTypeWithGroup,
} from '@/client/services/api/graphql/gql/graphql';

import type { ExpertFormData } from '../forms/types';
import { graphQLClient } from '@/client/services/api/clients/graphqlClient';
import { updateExpertMutation } from '../api/mutations/updateExpertMutation';
import { useMutation } from '@tanstack/react-query';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';
import { useExpertsStore } from '../expertsStore';

export const useUpdateExpert = () => {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { setUpdatingExpertModalIsOpen } = useExpertsStore();

  // TODO: Remove this once the getOneExpert graph returns the correct segmentation type
  const segmentationTypeWithGroupMap: Record<
    string | SegmentationTypeWithGroup,
    SegmentationTypeWithGroup
  > = {
    location: SegmentationTypeWithGroup.Location,
    [SegmentationTypeWithGroup.Location]: SegmentationTypeWithGroup.Location,
    department: SegmentationTypeWithGroup.Department,
    [SegmentationTypeWithGroup.Department]: SegmentationTypeWithGroup.Department,
    group: SegmentationTypeWithGroup.Group,
    [SegmentationTypeWithGroup.Group]: SegmentationTypeWithGroup.Group,
    team: SegmentationTypeWithGroup.Team,
    [SegmentationTypeWithGroup.Team]: SegmentationTypeWithGroup.Team,
    grade: SegmentationTypeWithGroup.Grade,
    [SegmentationTypeWithGroup.Grade]: SegmentationTypeWithGroup.Grade,
    org_level: SegmentationTypeWithGroup.OrgLevel,
    [SegmentationTypeWithGroup.OrgLevel]: SegmentationTypeWithGroup.OrgLevel,
  };

  interface UpdateExpertMutationVariables {
    expertId: string;
    formData: ExpertFormData;
  }

  const updateExpert = useMutation({
    mutationFn: async ({ expertId, formData }: UpdateExpertMutationVariables) => {
      const { color, description, enabled, name, segmentation, resources } = formData;

      const updateExpertPayload: ExpertUpdateInput = {
        color,
        description,
        enabled,
        name,
        segmentation: segmentation.map((segment) => ({
          segmentationId: segment.segmentationId,
          segmentationType: segmentationTypeWithGroupMap[segment.segmentationType],
        })),
        resources: resources.map((resource) => ({
          addedBy: resource.addedBy,
          extensionName: resource.extensionName,
          extensionType: resource.extensionType,
          id: resource.id,
          path: resource.path,
          source: resource.source,
          sourceId: resource.sourceId,
          downloadLink: resource.downloadLink,
          isFolder: resource.isFolder,
          fileCount: resource.fileCount,
          parentFolder: resource.parentFolder,
        })),
      };
      const data = await graphQLClient.request(updateExpertMutation, {
        expertId,
        updateBody: updateExpertPayload,
      });
      return data.updateExpert;
    },
    onSuccess: () => {
      setToast({
        show: true,
        status: 'success',
        title: t('admin.aiAgent.experts.updateSuccess'),
      });

      setUpdatingExpertModalIsOpen(true);
    },
    onError: () => {
      setToast({
        show: true,
        status: 'error',
        title: t('admin.aiAgent.experts.updateError'),
      });
    },
  });

  return {
    updateExpert,
  };
};
